import React, { createContext, useLayoutEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { get } from '../api/http';
import { Confirmation } from '../components/ui/Confirmation';
import { FontStyles } from '../fonts';
import { useContent } from '../hooks/useContent';

export const OfflineContext = createContext<undefined>(undefined);

export const OfflineContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const { getContentByPath } = useContent();
  const [offline, setOffline] = useState<boolean | undefined>(undefined);
  const [offlineMessage, setOfflineMessage] = useState(getContentByPath('offline.description'));

  useLayoutEffect(() => {
    const getInfo = async () => {
      const url = process.env.REACT_APP_MAINTENANCE_CHECK_URL;
      if (!url || process.env.IN_JEST) {
        window.mgm?.logger?.debug('Online Mode, no URL', { url });
        setOffline(false);
        return;
      }

      try {
        const response = await get(url, undefined, false, false);
        if (response.status === 200) {
          const json = await response.json();
          window.mgm?.logger?.debug('Setting Online Mode', { ...json });
          setOffline(json.enableDowntimeMode);
          setOfflineMessage(json.downtimeMessage);
        } else {
          window.mgm?.logger?.debug('Online Mode, maintenance error', {
            url,
            status: response.status,
            statusText: response.statusText,
            type: response.type,
          });
          setOffline(false);
        }
      } catch (e: unknown) {
        window.mgm?.logger?.error(e as Error, {
          url,
          message: 'Online Mode, maintenance error',
        });
        setOffline(false);
      }
    };

    getInfo();
  }, []);

  const offlineComponent = useMemo(
    () => (
      <>
        <FontStyles />
        <Confirmation
          noIcon={true}
          title=''
          description={<ReactMarkdown rehypePlugins={[rehypeRaw]}>{offlineMessage}</ReactMarkdown>}
          redirectCTA={{
            label: getContentByPath('offline.cta.label'),
            action: async () => {
              window.location.href = window.location.origin;
            },
          }}
        />
      </>
    ),
    [getContentByPath, offlineMessage],
  );

  const renderedChildren = useMemo(() => {
    if (offline === undefined) return <></>;
    return offline ? offlineComponent : children;
  }, [children, offline, offlineComponent]);

  return <OfflineContext.Provider value={undefined}>{renderedChildren}</OfflineContext.Provider>;
};
